import React, { useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { getSnapshot } from 'mobx-state-tree'
import { Box, IconButton } from '@mui/material'
import { ArrowForwardRounded, StopCircleRounded, UpdateRounded } from '@mui/icons-material'
import { GridCellParams } from '@mui/x-data-grid-pro'
import { Link } from 'react-router-dom'

import { useStores } from '@trivie/core'
import { DataTable } from '@trivie/ui-web/components/data-table'
import { initColumns } from '@trivie/ui-web/components/data-table/configure'
import { Toolbar } from '@trivie/ui-web/components/table-toolbar'
import { Text } from '@trivie/ui-web/components/text'
import { useConfirm } from '@trivie/ui-web/components/dialogs/confirmation-dialog'
import { ExtendAssignment } from '@trivie/ui-web/components/extend-dialog'

import { SubscriptionChip } from './subscription-chip'

interface SubscriptionUserPurchasesTableProps {
  userId: number
}

export const SubscriptionUserPurchasesTable = observer(
  ({ userId }: SubscriptionUserPurchasesTableProps) => {
    const { subscriptionStore, manageUserStore } = useStores()
    const { permissions } = manageUserStore

    const confirm = useConfirm()

    const [selectedRows, setSelectedRows] = useState<any>([])
    const [tableActions, setTableActions] = useState<any>([])
    const [showExtend, setShowExtend] = useState(false)

    useEffect(() => {
      subscriptionStore.getSubscriptionsForUser(userId)
    }, [userId])

    const rows = subscriptionStore.userSubscriptions.map((p) => ({
      ...getSnapshot(p),
      status: p.status,
    }))

    const colDefs: any[] = []

    colDefs.push(
      {
        field: 'title',
        headerName: 'Subscription',
        description: 'Subscription title',
        data_type: 'str',
        width: 200,
      },
      {
        field: 'purchased_date',
        headerName: 'Purchase Date',
        description: 'Date purchased',
        data_type: 'datetime',
        width: 150,
      },
      {
        field: 'expiration_date',
        headerName: 'Expires On',
        description: 'Expiration date',
        data_type: 'datetime',
        width: 150,
      },
    )

    colDefs.push({
      field: 'status',
      headerName: 'Status',
      description: 'Purchase status',
      data_type: 'str',
      possible_values: ['Active', 'Expired', 'Expiring', 'Failed'],
      operators: ['eq', 'ne'],
      width: 120,
      renderCell: (params: GridCellParams) => <SubscriptionChip status={params.value as string} />,
    })

    const { columns: baseColumns } = initColumns(colDefs)

    const columns = [
      ...baseColumns,
      {
        field: '_action',
        headerName: '',
        width: 50,
        sortable: false,
        filterable: false,
        resizable: false,
        disableColumnMenu: true,
        disableReorder: true,
        renderCell: (params: GridCellParams) => (
          <Link to={`/activities/subscriptions/purchases/${params.row.id}/overview`}>
            <IconButton size="small">
              <ArrowForwardRounded fontSize="small" />
            </IconButton>
          </Link>
        ),
      },
    ]

    const getActions = () => {
      const selectedStatus = rows.find((p) => p.id === selectedRows[0])?.status
      const allActiveOrExpiring = selectedRows.every((id: number) => {
        const purchase = rows.find((p) => p.id === id)
        return purchase?.status === 'active' || purchase?.status === 'expiring'
      })

      return [
        {
          label: 'Extend',
          icon: UpdateRounded,
          disabled:
            rows.find((p) => p.id === selectedRows[0])?.expiration_date == null ||
            selectedRows.length !== 1 ||
            selectedStatus === 'failed' ||
            selectedStatus === 'expired' ||
            !permissions?.permissions.some((p) => p.code === 'can_edit_purchase'),

          onClick: () => setShowExtend(true),
        },
        {
          label: 'Revoke Subscription',
          color: 'danger',
          icon: StopCircleRounded,
          disabled:
            selectedRows.length !== 1 ||
            !allActiveOrExpiring ||
            !permissions?.permissions.some((p) => p.code === 'can_delete_purchase'),
          onClick: () => {
            confirm({
              danger: true,
              title: 'Revoke Subscription',
              dialogText:
                'Are you sure you want to revoke the selected subscription(s)? This action cannot be undone.',
            })
              .then(async () => {
                const success = await subscriptionStore.revokeSubscription(selectedRows[0])
                setSelectedRows([])
                if (!success) {
                  confirm({
                    danger: true,
                    title: 'Error',
                    dialogText: 'There was an issue revoking the selected subscription(s)',
                    hideCancelButton: true,
                  })
                } else {
                  subscriptionStore.getSubscriptionsForUser(userId)
                }
              })
              .catch(() => {})
          },
        },
      ]
    }

    useEffect(() => {
      setTableActions(getActions())
    }, [selectedRows])

    return (
      <Box mb="40px">
        <DataTable
          // loading={subscriptionStore.loadingPurchases}
          toolbar={<Toolbar selected={selectedRows} actions={tableActions} />}
          searchPlaceholder={'Search subscriptions'}
          columns={columns as any}
          rows={rows}
          getRowId={(row) => row.id}
          onRowSelectionModelChange={(param) => setSelectedRows(param)}
          rowSelectionModel={selectedRows}
          checkboxSelection
          onSaveFilters={() => {}}
          disableColumnPinning={false}
          pinnedColumns={{ right: ['_action'] }}
          sx={{
            '& .MuiDataGrid-cell--pinnedRight': {
              borderLeft: 'none',
              boxShadow: 'none',
              backgroundColor: '#fff',
            },
            '& .MuiDataGrid-row:hover .MuiDataGrid-cell--pinnedRight': {
              backgroundColor: 'rgb(245,245,245) !important',
            },
            '& .MuiDataGrid-row.Mui-selected .MuiDataGrid-cell--pinnedRight': {
              backgroundColor: 'rgb(239, 242, 254)!important',
            },
            '& .MuiDataGrid-row.Mui-selected:hover .MuiDataGrid-cell--pinnedRight': {
              backgroundColor: 'rgb(230, 235, 253)!important',
            },
            '& .MuiDataGrid-columnHeader--pinnedRight': {
              borderLeft: 'none',
              boxShadow: 'none',
              backgroundColor: 'inherit',
            },
            '& .MuiDataGrid-columnHeader[data-field="_action"] .MuiDataGrid-columnSeparator': {
              display: 'none',
            },
            '& .MuiDataGrid-columnHeader--last .MuiDataGrid-columnSeparator': {
              display: 'none',
            },
          }}
        />
        {showExtend && (
          <ExtendAssignment
            title="Extend Subscription"
            open={showExtend}
            initial={rows.find((p) => p.id === selectedRows[0])?.expiration_date}
            onClose={() => {
              setShowExtend(false)
              setSelectedRows([])
            }}
            onExtend={async ({ endDateTime }) => {
              const success = await subscriptionStore.extendPurchases(selectedRows, endDateTime)
              setShowExtend(false)
              setSelectedRows([])
              if (!success) {
                confirm({
                  danger: true,
                  title: 'Error',
                  dialogText: 'There was an issue extending this purchase',
                  hideCancelButton: true,
                })
              } else {
                subscriptionStore.getSubscriptionsForUser(userId)
              }
            }}
          />
        )}
      </Box>
    )
  },
)
